import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { Subscription, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import lodash from 'lodash';

import { TimeService } from '@bayclubs/utils';
import { FiltersStorageService } from '@shared/shared.module';
import { GroupClassesSecurityService } from 'src/app/infrastructure/infrastructure.module';

import { ClubCentricFilters } from '../../models/club-centric-report-model';
import { ClubCentricClass, ReportFilter, ReportModel, ReportType } from '../../models/reports-model';
import { ReportSelectService } from '../../services/report-select.service';

import { AdpPayPeriodModel, ClassTypeModel, ClubModel, ContextModel, InstructorModel, OnlineContentViewType } from '@bayclubs/group-classes-employee-api';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-reports-filter',
    templateUrl: './reports-filter.component.html',
    styleUrls: ['./reports-filter.component.scss'],
    standalone: false
})
export class ReportsFilterComponent implements OnInit, OnDestroy {
    private _classes: ClubCentricClass[];
    @Input()
    set classes(value: ClubCentricClass[]) {
        this._classes = value;
        if (this.form && this.form.value.className) {
            if (this._classes.filter(p => p.name === this.form.value.className).length === 0) {
                const classesControl = this.form.controls['className'];
                if (classesControl) {
                    classesControl.setValue(null, { emitEvent: false });
                }
            }
        }
    }
    get classes(): ClubCentricClass[] {
        return this._classes;
    }
    private _instructors: InstructorModel[];
    @Input()
    set instructors(value: InstructorModel[]) {
        this._instructors = value;
        if (this.form && this.form.value.instructors) {
            const instructorControl = this.form.controls['instructors'];
            const selectedInstructors: string[] = this.form.value.instructors;

            if (instructorControl) {
                const justSetInstructorIds: string[] = this._instructors.map(i => i.id);
                const shouldCleanSelection: boolean = selectedInstructors.some(
                    setInstructorId => !justSetInstructorIds.includes(setInstructorId)
                );
                if (shouldCleanSelection) {
                    instructorControl.setValue([], { emitEvent: false });
                }
            }
        }
    }
    get instructors(): InstructorModel[] {
        return this._instructors;
    }
    @Input()
    selectedReport: ReportModel;
    @Input()
    generateDisabled: boolean;
    @Input()
    showClassFilter: boolean;

    @Output()
    doFilter: EventEmitter<ReportFilter> = new EventEmitter<ReportFilter>();
    @Output()
    filtersSelected: EventEmitter<ClubCentricFilters> = new EventEmitter<ClubCentricFilters>();
    @Output()
    generateReport: EventEmitter<ReportFilter> = new EventEmitter<ReportFilter>();
    @Output()
    generateAdpExtract: EventEmitter<ReportFilter> = new EventEmitter<ReportFilter>();

    instructorsMulti: boolean;
    form: UntypedFormGroup;
    reportSelectForm: UntypedFormGroup;
    reports: ReportModel[];
    subscriptions: Subscription;
    reportType = ReportType;
    public context: ContextModel;

    public classTypes: ClassTypeModel[];
    public contentTypes: Array<any> = [
        {
            name: this.translateService.instant('OnlineContentViewReport.All'),
            code: OnlineContentViewType.All
        },
        {
            name: this.translateService.instant('OnlineContentViewReport.OnlineClass'),
            code: OnlineContentViewType.OnlineClass
        },
        {
            name: this.translateService.instant('OnlineContentViewReport.RecordedClass'),
            code: OnlineContentViewType.RecordedClass
        }
    ];

    public payPeriods: AdpPayPeriodModel[] = [];

    public clubs: ClubModel[];

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private reportService: ReportSelectService,
        private filtersStorageService: FiltersStorageService,
        private securityService: GroupClassesSecurityService,
        private timeService: TimeService,
        private translateService: TranslateService
    ) { }

    ngOnInit() {
        this.instructorsMulti = this.shouldInstructorsFilterBeMultiSelect(this.selectedReport);
        this.subscriptions = new Subscription();
        this.context = lodash.cloneDeep(this.activatedRoute.snapshot.data.context);
        this.initDataForSelects();
        this.reports = this.reportService.getReports();
        this.initPayPeriods();
        this.initReportSelectForm();
        this.initForm();
        this.subscribeToQueryParamChanges();
    }
    ngOnDestroy() {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }



    initDataForSelects() {
        this.securityService.filterClubsForReports(this.context.clubs).subscribe(filtered => {
            this.clubs = filtered;
            this.filterInstructorsByHomeClubForPayRollReport();
        });
        this.classTypes = this.context.classTypes;
    }

    private initPayPeriods() {
        this.payPeriods = this.context.adpPayPeriods;
    }

    private filterInstructorsByHomeClubForPayRollReport() {
        let clubIds = this.clubs.map(x => x.id);

        if (this.form && this.form.value) {
            const formClubs: string[] = this.form.value.clubs;
            if (formClubs && formClubs.length > 0) {
                clubIds = clubIds.filter(c => formClubs.includes(c));
            }
        }

        this.instructors = this.context.instructors;
        if (this.shouldFilterInstructorsByHomeClub) {
            this.instructors = this.instructors.filter(i => clubIds.includes(i.homeClubId));
        }
        if (this.selectedReport) {
            if (this.selectedReport.type === ReportType.AttendanceReport) {
                this.filterInstructorsByUserAllowedHomeClubs();
            }
        }
    }

    initReportSelectForm() {
        let reportId = null;
        if (this.selectedReport) {
            reportId = this.selectedReport.id;
        }
        this.reportSelectForm = this.formBuilder.group({
            report: [reportId, []]
        });

        this.reportSelectForm.valueChanges.subscribe(value => {
            const report = this.reports.find(p => p.id === value.report);
            if (report) {
                const rememberedUrl = this.getRememberedUrl(report.type);
                if (rememberedUrl) {
                    this.router.navigateByUrl(rememberedUrl);
                } else {
                    this.router.navigate([report.route]);
                }
            }
        });
    }

    initForm() {
        const firstDayOfPreviousMonth = this.timeService.toBcDate(
            this.timeService.startOfMonth(this.timeService.addMonths(this.timeService.nowUtc(), -1))
        );
        const lastDayOfPreviousMonth = this.timeService.toBcDate(
            this.timeService.endOfMonth(this.timeService.addMonths(this.timeService.nowUtc(), -1))
        );

        const payPeriodToSelect = this.showPayPeriodFilter ? this.payPeriods[this.payPeriods.length - 1] : null;

        this.form = this.formBuilder.group(
            {
                contentType: [OnlineContentViewType.All, []],
                dateFrom: [payPeriodToSelect ? payPeriodToSelect.dateFrom : firstDayOfPreviousMonth, [Validators.required]],
                dateTo: [payPeriodToSelect ? payPeriodToSelect.dateTo : lastDayOfPreviousMonth, [Validators.required]],
                clubs: [null, []],
                classType: [null, []],
                instructors: [null, []],
                className: [null, []],
                payPeriod: [payPeriodToSelect?.id, []]
            },
            { validator: this.dateLessThan('dateFrom', 'dateTo') }
        );

        this.subscriptions.add(
            this.form.get('className').valueChanges.subscribe(value => {
                const filters = new ClubCentricFilters(value, this.form.value.instructors);
                this.filtersSelected.emit(filters);
            })
        );

        if (this.selectedReport) {
            if (this.selectedReport.type === ReportType.ClubCentricReport || this.selectedReport.type === ReportType.AttendanceReport) {
                this.subscriptions.add(
                    this.form.get('instructors').valueChanges.subscribe(value => {
                        const filters = new ClubCentricFilters(this.form.value.className, value);
                        this.filtersSelected.emit(filters);
                    })
                );
            }

            this.filterData();
        }
        this.subscribeToFormChanges();
    }

    private filterData() {
        if (this.selectedReport.type === ReportType.ClubCentricReport || this.selectedReport.type === ReportType.AttendanceReport) {
            this.subscriptions.add(
                merge(
                    this.form.controls['dateFrom'].valueChanges,
                    this.form.controls['dateTo'].valueChanges,
                    this.form.controls['clubs'].valueChanges,
                    this.form.controls['classType'].valueChanges
                )
                    .pipe(debounceTime(500))
                    .subscribe(value => {
                        if (this.form.valid) {
                            this.showReport();
                        } else {
                            this.clearReportData();
                        }
                    })
            );
        } else {
            this.form.valueChanges.pipe(debounceTime(500)).subscribe(value => {
                if (this.form.valid) {
                    this.showReport();
                } else {
                    this.clearReportData();
                }
            });
        }
    }

    private clearReportData() {
        const classesControl = this.form.controls['className'];
        if (classesControl) {
            classesControl.setValue(null, { emitEvent: false });
        }
        this.doFilter.emit(null);
    }
    private dateLessThan(from: string, to: string) {
        return (group: UntypedFormGroup): { [key: string]: any } => {
            const fromDateControl = group.controls[from];
            const toDateControl = group.controls[to];
            let fromDate;
            if (fromDateControl.value) {
                fromDate = new Date(fromDateControl.value.value);
            }
            let toDate;
            if (toDateControl.value) {
                toDate = new Date(toDateControl.value.value);
            }
            if (fromDate > toDate) {
                return {
                    dates: true
                };
            }
            return {};
        };
    }

    private subscribeToQueryParamChanges() {
        if (this.selectedReport) {
            this.subscriptions.add(
                this.activatedRoute.queryParamMap.pipe(debounceTime(20)).subscribe(params => {
                    this.subscribeToInstructorFilter(this.selectedReport, params);

                    const dateFromParam: string = params.get('dateFrom');
                    if (dateFromParam) {
                        const dateFromControl = this.form.controls['dateFrom'];
                        const dateObj = { value: dateFromParam };
                        dateFromControl.setValue(dateObj, { emitEvent: false });
                    }

                    const dateToParam: string = params.get('dateTo');
                    if (dateToParam) {
                        const dateToControl = this.form.controls['dateTo'];
                        const dateObj = { value: dateToParam };
                        dateToControl.setValue(dateObj, { emitEvent: false });
                    }

                    const clubs: string[] = params.getAll('clubIds');
                    const clubsControl = this.form.controls['clubs'];
                    clubsControl.setValue(clubs || [], { emitEvent: false });
                    this.filterInstructorsByHomeClubForPayRollReport();

                    const classesControl = this.form.controls['className'];
                    if (classesControl) {
                        classesControl.setValue(null, { emitEvent: false });
                    }

                    const classTypeControl = this.form.controls['classType'];
                    if (classTypeControl) {
                        const classTypeId = params.get('classType');
                        classTypeControl.setValue(classTypeId, { emitEvent: false });
                    }

                    const contentTypeControl = this.form.controls['contentType'];
                    if (contentTypeControl) {
                        const val = params.get('contentType');
                        const contentTypeControlVal = val !== null ? Number(val) : null;
                        contentTypeControl.setValue(contentTypeControlVal, { emitEvent: false });
                    }

                    const payPeriodControl = this.form.controls['payPeriod'];
                    if (payPeriodControl) {
                        const payPeriodVal = params.get('payPeriod');
                        if (payPeriodVal) {
                            payPeriodControl.setValue(payPeriodVal, { emitEvent: false });
                        }

                    }

                    this.showReport();
                }

                )

            );
        }
    }

    showReport() {
        const classesControl = this.form.controls['className'];
        if (classesControl) {
            classesControl.setValue(null, { emitEvent: false });
        }


        this.doFilter.emit({
            dateFrom: this.form.value.dateFrom,
            dateTo: this.form.value.dateTo,
            clubIds: this.form.value.clubs,
            instructorId: this.form.value.instructors,
            className: this.form.value.className,
            instructorIds: this.form.value.instructors,
            classTypeId: this.form.value.classType,
            contentType: this.form.value.contentType
        });
    }

    generateReportFile() {
        this.generateReport.emit({
            dateFrom: this.form.value.dateFrom,
            dateTo: this.form.value.dateTo,
            clubIds: this.form.value.clubs,
            instructorId: this.form.value.instructors,
            className: this.form.value.className,
            instructorIds: this.form.value.instructors,
            classTypeId: this.form.value.classType,
            contentType: this.form.value.contentType
        });
    }



    generateAdpExtractFile() {
        this.generateAdpExtract.emit({
            dateFrom: this.form.value.dateFrom,
            dateTo: this.form.value.dateTo,
            clubIds: this.form.value.clubs,
            instructorId: null,
            className: null,
            instructorIds: null,
            classTypeId: this.form.value.classType,
            contentType: this.form.value.contentType
        });
    }

    public get isAdpExtractVisible() {
        return false;
    }

    private subscribeToFormChanges() {
        if (this.selectedReport) {
            switch (this.selectedReport.type) {
                case ReportType.PayrollReport:
                case ReportType.InstructorCentricReport:
                    this.subscriptions.add(
                        this.form.controls['instructors'].valueChanges.subscribe(value => {
                            this.mergeQueryParam({ instructors: value });
                        })
                    );
                    break;
                case ReportType.AdpReport:
                    this.subscriptions.add(
                        this.form.controls['payPeriod'].valueChanges.subscribe(value => {

                            const payPeriod = this.payPeriods.find(x => x.id == value);
                            if (payPeriod) {
                                this.form.controls['dateFrom'].setValue(payPeriod.dateFrom)
                                this.form.controls['dateTo'].setValue(payPeriod.dateTo)
                            }
                            this.mergeQueryParam({ payPeriod: value });
                        })
                    );
                    break;
                default:
                    break;
            }
        }

        if (!this.showPayPeriodFilter) {
            this.subscriptions.add(
                this.form.controls['dateFrom'].valueChanges.subscribe(value => {
                    if (value) {
                        this.mergeQueryParam({ dateFrom: value.value });
                    } else {
                        this.mergeQueryParam({ dateFrom: null });
                    }
                })
            );

            this.subscriptions.add(
                this.form.controls['dateTo'].valueChanges.subscribe(value => {
                    if (value) {
                        this.mergeQueryParam({ dateTo: value.value });
                    } else {
                        this.mergeQueryParam({ dateTo: null });
                    }
                })
            );

        }


        this.subscriptions.add(
            this.form.controls['clubs'].valueChanges.subscribe(value => {
                this.mergeQueryParam({ clubIds: value });
            })
        );

        this.subscriptions.add(
            this.form.controls['classType'].valueChanges.subscribe(value => {
                this.mergeQueryParam({ classType: value });
            })
        );

        this.subscriptions.add(
            this.form.controls['contentType'].valueChanges.subscribe(value => {
                this.mergeQueryParam({ contentType: value });
            })
        );

    }

    private mergeQueryParam(params: Params): void {
        this.router
            .navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: params,
                queryParamsHandling: 'merge'
            })
            .then(() => {
                this.filtersStorageService[this.getFiltersStorageUrlKey(this.selectedReport.type)] = this.router.url;
            });
    }

    public get shouldFilterInstructorsByHomeClub() {
        if (this.selectedReport) {
            switch (this.selectedReport.type) {
                case ReportType.PayrollReport:
                    return false;
                case ReportType.AttendanceReport:
                    return false;
                case ReportType.ClubCentricReport:
                    return false;
                case ReportType.InstructorCentricReport:
                    return true;
                case ReportType.OnlineContentViewReport:
                    return false;
                default:
                    return false;
            }
        } else {
            return false;
        }
    }

    public get shouldShowInstructorsFilter() {

        if (this.selectedReport && this.selectedReport.type !== ReportType.PayrollReport && this.selectedReport.type !== ReportType.OnlineContentViewReport && this.selectedReport.type !== ReportType.AdpReport) {
            return true;
        }

        return false;
    }

    public get shouldFilterMultipleClubs() {
        if (this.selectedReport) {
            if (this.selectedReport.type !== ReportType.PayrollReport && this.selectedReport.type !== ReportType.OnlineContentViewReport) {
                return true;
            }
        } else {
            return true;
        }

        return false;
    }

    public get noReportSelected() {
        return this.selectedReport === undefined;
    }

    public get showOnlineContentTypeFilter() {
        return this.selectedReport && this.selectedReport.type === ReportType.OnlineContentViewReport;
    }

    public get showClubFilter() {
        return this.selectedReport && this.selectedReport.type !== ReportType.OnlineContentViewReport;
    }

    public get showClassTypeFilter() {
        return this.selectedReport && this.selectedReport.type !== ReportType.OnlineContentViewReport && this.selectedReport.type !== ReportType.AdpReport;
    }

    public get showInstructorHomeClubLabel() {
        return this.shouldFilterInstructorsByHomeClub;
    }

    public get showDatesFilter() {
        return this.selectedReport;
    }

    public get showPayPeriodFilter() {
        return this.selectedReport && this.selectedReport.type === ReportType.AdpReport;
    }


    private filterInstructorsByUserAllowedHomeClubs(): void {
        const clubIds = this.clubs.map(x => x.id);
        this.instructors = this.instructors.filter(i => clubIds.includes(i.homeClubId));
    }

    private shouldInstructorsFilterBeMultiSelect(selectedReport: ReportModel): boolean {
        if (selectedReport) {
            switch (selectedReport.type) {
                case ReportType.ClubCentricReport:
                    return false;
                case ReportType.PayrollReport:
                    return true;
                case ReportType.InstructorCentricReport:
                    return true;
                case ReportType.AdpReport:
                    return true;
                case ReportType.AttendanceReport:
                    return false;
                default:
                    return false;
            }
        } else {
            return false;
        }
    }

    private getRememberedUrl(reportType: ReportType): string {
        return this.filtersStorageService[this.getFiltersStorageUrlKey(reportType)];
    }

    private subscribeToInstructorFilter(selectedReport: ReportModel, params: ParamMap): void {
        switch (selectedReport.type) {
            case ReportType.ClubCentricReport:
            case ReportType.AttendanceReport:
                const instructorIdParam: string = params.get('instructors');

                if (instructorIdParam) {
                    const instructorControl = this.form.controls['instructors'];
                    instructorControl.setValue(instructorIdParam, { emitEvent: false });
                }

                break;
            case ReportType.PayrollReport:
            case ReportType.InstructorCentricReport:
                const instructorIdsParam: string[] = params.getAll('instructors');
                if (instructorIdsParam) {
                    const instructorControl = this.form.controls['instructors'];
                    instructorControl.setValue(instructorIdsParam, { emitEvent: false });
                }
                break;
            default:
                break;
        }
    }

    private getFiltersStorageUrlKey(reportType: ReportType): string {
        switch (reportType) {
            case ReportType.ClubCentricReport:
                return 'clubCentricReportUrl';
            case ReportType.PayrollReport:
                return 'payrollReportUrl';
            case ReportType.InstructorCentricReport:
                return 'instructorCentricReportUrl';
            case ReportType.AttendanceReport:
                return 'attendanceReportUrl';
            case ReportType.OnlineContentViewReport:
                return 'onlineContentViewReportUrl';
            case ReportType.AdpReport:
                return 'adpReportUrl';
            default:
                return '';
        }
    }
}
